// @flow
import React, { useCallback, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useMount } from 'react-use';

import CircularProgress from '@material-ui/core/CircularProgress';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import ListItemText from '@material-ui/core/ListItemText';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';

import SearchIcon from '@material-ui/icons/Search';

import Downshift from 'downshift';
import { Field, Formik, Form } from 'formik';

import { featureList } from 'components/Dialogs/WFDesktopFeatures/featuresConfig';
import SingleFeatureModal from 'components/Dialogs/WFDesktopFeatures/SingleFeatureModal';
import FormikTextField from 'components/Formik/TextField';
import QButton from 'components/QButton';

import { getDatasetsById } from 'data/datasets/selectors';
import { getTopTierEntitlement } from 'data/entitlements/entitlementsSelectors';
import { getPopularInstitutions } from 'data/institutions/selectors';
import {
  getPopularInstitutions as getPopularInstitutionsAction,
  searchForInstitutions as searchForInstitutionsAction,
} from 'data/institutions/actions';

import { isAcme } from 'isAcme';
import { bindPromiseAction } from 'utils/actionHelpers';
import { getDatasetId } from 'utils/auth/selectors';
import { getLogger } from 'utils/logger';
import tracker from 'utils/tracker';

import { institutionSelected } from '../actions';
import * as selectors from '../selectors';

import { BankImageCell, BankImagesGrid, useStyles } from './styles';

const log = getLogger('InstitutionPicker/index.js');

type Props = {
  onAddUnconnectedAccount: () => void,
  trackingProperties: any,
}

function extractHostname(url) {

  let hostname;

  // find & remove protocol (http, ftp, etc.) and get hostname
  if (url.indexOf("//") > -1) {
    hostname = url.split('/')[2];
  }
  else {
    hostname = url.split('/')[0];
  }

  // find & remove any port number
  hostname = hostname.split(':')[0];

  // find & remove any query "?"
  hostname = hostname.split('?')[0];

  // find & remove any beginning www.
  if (hostname.indexOf('www.') === 0) {
    hostname = hostname.slice(4);
  }

  return hostname;
}

function renderSuggestion(params) {
  const { suggestion, key, itemProps, highlightedIndex, selectedItem } = params;
  const isHighlighted = highlightedIndex === key;
  const isSelected = selectedItem && selectedItem.name === suggestion.name;

  const url = suggestion.url ? extractHostname(suggestion.url) : null;

  return (
    <MenuItem
      {...itemProps}
      key={key}
      id={`institution-suggest-${key}`}
      selected={isHighlighted}
      component="div"
      style={{
        fontWeight: isSelected ? 500 : 400,
      }}
    >
      <ListItemText
        primary={suggestion.name}
        secondary={url}
      />
    </MenuItem>
  );
}

const keyHandle = (highlightedIndex, selectHighlightedItem) => (e) => {
  if (e.keyCode === 9 && highlightedIndex !== null) {
    e.preventDefault();
    selectHighlightedItem();
  }
};

const handleSelect = (institution, trackingProperties, dispatch) => {
  if (institution) {
    dispatch(institutionSelected(institution.id));

    let institutionId;
    if (institution.ofx) {
      institutionId = institution.ofx?.bid;
    } else if (institution.fds) {
      institutionId = institution.fds?.id;
    } else {
      institutionId = institution.id;
    }
    tracker.track(tracker.events.addFISelect, {
      ...trackingProperties,
      institution_id: institutionId,
      institution_name: institution.name,
    });
  }
};

const handleSubmit = (values, actions) => {
  log.log('Called onSubmit... This does nothing so just resetting form');
  actions.setSubmitting(false);
};

const InstitutionSelectorForm = (props: Props) => {
  const {
    onAddUnconnectedAccount,
    trackingProperties,
  } = props;

  const dispatch = useDispatch();

  const isSubmitting = useSelector(selectors.getIsSubmitting);
  const popularInstitutions = useSelector(getPopularInstitutions);
  const tier = useSelector(getTopTierEntitlement);

  const datasetId = useSelector((state) => getDatasetId(state), shallowEqual);
  const datasetsById = useSelector((state) => getDatasetsById(state), shallowEqual);

  const [featureModal, setFeatureModal] = useState(undefined);
  const [institutions, setInstitutions] = useState();
  const [lastSearchValue, setLastSearchValue] = useState();

  const classes = useStyles();

  useMount(() => {
    dispatch(getPopularInstitutionsAction());
  });

  const searchForInstitutionsPromise = useCallback(bindPromiseAction(dispatch, searchForInstitutionsAction));
  const searchForInstitutions = (input) => {
    if (input && input.length > 0) {
      searchForInstitutionsPromise(input)
        .then((resolveInstitutions) => setInstitutions(resolveInstitutions))
        .catch((error) => log.error(error));
      setLastSearchValue(input);
    } else {
      // Note: This gets called when the control leaves focus. This can happen clicking away or even by selecting
      // item. In this case, input will not exists. However, on screen, input is still left in control (perhaps
      // interaction with material-ui input isn't setup correctly (not really sure). Also, I don't clear last
      // search term here because when we select an item, then it will always be cleared due to this behavior.
      //
      setInstitutions(null);
    }
  };

  const datasetObject = datasetsById && datasetsById.get(datasetId);
  const OSBrandKey = (datasetObject && datasetObject.platform || null) === 'QMAC_MWF' ? 'mac' : 'win';

  const getFeature = () => featureList && featureList[OSBrandKey].find((x)=> x.id === 'investmentLoanAccount');
  const openDoMoreQuickenModal = (featureValue = getFeature()) => () => setFeatureModal(featureValue);
  const closeDoMoreQuickenModal = () => setFeatureModal(undefined);

  const renderAddManually = (className) => ((!institutions || institutions.size === 0) ?
    <Typography variant="subtitle1" className={className}>
      Can&apos;t find your financial institution? &nbsp;
      <QButton id="add_manually_btn" onClick={onAddUnconnectedAccount} className={classes.manualButton}>
        Add manually.
      </QButton>
    </Typography> : null);

  return (
    <>
      {isSubmitting &&
        <div className={classes.statusContainer} >
          <CircularProgress size={100} className={classes.progress} />
        </div>
      }
      <Formik
        initialValues={{}}
        onSubmit={(values, actions) => handleSubmit(values, actions)}
        render={(formikBag) => {
          const { handleSubmit: formikHandleSubmit } = formikBag;
          return (
            <Form onSubmit={formikHandleSubmit} className={classes.root}>
              <Typography variant="body1" className={classes.subTitle}>
                {`Connect your financial institution to ${isAcme ? 'Simplifi' : 'Quicken'}`}
              </Typography>
              <Downshift
                initialHighlightedIndex={0}
                itemToString={(item) => item ? item.label : ''}
                onInputValueChange={(input) => searchForInstitutions(input, dispatch)}
                onSelect={(item) =>
                  handleSelect(
                    item,
                    { ...trackingProperties, selected_by: 'search', last_search_value: lastSearchValue },
                    dispatch
                  )
                }
              >
                {({ getInputProps, getItemProps, reset, selectedItem, highlightedIndex, selectHighlightedItem }) => {
                  const { onBlur, onChange, value, ...inputProps } = getInputProps();
                  return (
                    <div>
                      <Field
                        component={FormikTextField}
                        onFieldBlur={onBlur}
                        onFieldChange={onChange}
                        onKeyDown={keyHandle(highlightedIndex, selectHighlightedItem)}
                        fullWidth
                        margin="dense"
                        variant="outlined"
                        InputProps={{
                          autoFocus: true,
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="Clear search"
                                onClick={reset}
                              >
                                <SearchIcon />
                              </IconButton>
                            </InputAdornment>
                          ),
                          ...inputProps,
                          inputProps: {
                            id: "bankSearch",
                          },
                        }}
                        label="Search all financial institutions"
                        name="institution"
                        className={classes.searchBar}
                      />
                      {institutions ? (
                        <Paper square className={classes.dropDown}>
                          {institutions.map((institution, key) => (
                            renderSuggestion({
                              suggestion: institution,
                              key,
                              itemProps: getItemProps({ item: institution }),
                              highlightedIndex,
                              selectedItem,
                            })
                          ))}
                        </Paper>
                      ) : null}
                    </div>
                  );
                }}
              </Downshift>
              <Typography variant="body2" className={classes.popularCopy}>
                {`Or select from popular ones`}
              </Typography>
              {(!institutions || institutions.size === 0) && popularInstitutions && popularInstitutions.size &&
                <BankImagesGrid>
                  {popularInstitutions.map((institution) => (
                    <BankImageCell key={institution.id}>
                      <button
                        className={classes.popularButton}
                        onClick={() => handleSelect(institution, { ...trackingProperties, selected_by: 'icon' }, dispatch)}
                        type="button"
                      >
                        <img alt={institution.name} src={institution.logoUrl}/>
                      </button>
                    </BankImageCell>
                  ))}
                </BankImagesGrid>
              }
              {isAcme && renderAddManually(classes.addNonConnectedAccount)}
            </Form>
          );
        }}
      />
      {!isAcme && (
        <div className={classes.doMoreQuicken}>
          {renderAddManually(classes.addNonConnectedAccountQuicken)}
          {tier && tier.id && ['US_QUICKEN_DELUXE','CA_QUICKEN_DELUXE'].includes(tier.id) && <>
            <QButton
              onClick={openDoMoreQuickenModal()}
              className={classes.doMoreQuickenButton}>
              How do I add my investments or loans?
            </QButton>
            <SingleFeatureModal feature={featureModal} onClose={closeDoMoreQuickenModal} showDownloadButton/>
          </>
          }
        </div>
      )}
    </>
  );
};

export default InstitutionSelectorForm;
